import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import toastManager from '@/lib/toast/toastManager.js';
import answerConnectionCard from '@/redux/actions/pieveActions/answerConnectionCard.js';
import { threadTimeline } from '@/redux/selectors/pieveSelectors/assistantSelectors.js';
import { routeThreadId } from '@/redux/selectors/pieveSelectors/routeSelectors.js';
import { threadContent } from '@/redux/selectors/pieveSelectors/threadCoreSelectors.js';
import { isThreadRunning } from '@/redux/selectors/pieveSelectors/threadRunSelectors.js';
import { query } from '@/redux/selectors/uiSelectors.js';
import CONNECTION_RETURN_QUERY_KEY from '../lib/connectionReturnQueryKey.js';
import resolveConnectionReturn from '../lib/resolveConnectionReturn.js';

/**
 * A provider sign-in started from a connection card comes back to the conversation with
 * `?connected=<card>`. The OAuth callback has already recorded the card as connected, and it starts no
 * run itself (the person decides when Pieve goes on). Coming back from the sign-in is that decision:
 * this answers the card through a run, as the earlier frontend did when its sign-in window closed,
 * so Pieve picks the request back up. It runs once, then drops the key from the address.
 */
const ConnectionReturnHandler = () => {
  const dispatch = useDispatch();
  const [, setSearchParams] = useSearchParams();
  const threadId = useSelector(routeThreadId);
  const interactionId = useSelector((state) => query(state, CONNECTION_RETURN_QUERY_KEY)) || '';
  const blocks = useSelector((state) => threadTimeline(state, threadId));
  // Not `Array.isArray(activity)`: every listed thread gets an empty activity before its events arrive.
  const isLoaded = useSelector((state) => threadContent(state, threadId)?.eventsHydrated === true);
  const isRunning = useSelector((state) => isThreadRunning(state, threadId));
  const handled = useRef('');
  const decision = interactionId ? resolveConnectionReturn({ blocks, interactionId, isLoaded, isRunning }) : 'none';
  const appName = blocks.find((block) => block.kind === 'connection' && block.connection.interactionId === interactionId)?.connection.connectionSetup.app.name || 'the app';

  useEffect(() => {
    if (decision === 'none' || decision === 'wait' || handled.current === interactionId) {
      return;
    }

    handled.current = interactionId;
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete(CONNECTION_RETURN_QUERY_KEY);
        return next;
      },
      { replace: true }
    );

    if (decision !== 'continue') {
      return;
    }

    const patch = { outcome: 'connected', respondedAt: new Date().toISOString(), summary: `Connected to ${appName}.` };
    dispatch(answerConnectionCard({ threadId, interactionId, patch, optimistic: false })).then((res) => {
      if (res?.success === false) {
        toastManager.add({ title: `${appName} is connected`, description: 'Pieve did not pick the request back up. Send a message to continue.', type: 'error' });
      }
    });
  }, [decision, interactionId, threadId, appName, dispatch, setSearchParams]);

  return null;
};

export default ConnectionReturnHandler;
